/**
 * Normalize a session/methodology/guide markdown body before rendering.
 *
 * Strips two patterns that leak in from the import pipeline and would
 * otherwise show up as raw characters in the UI:
 *
 *   1. HTML comment breadcrumbs like `<!-- מקור: לקוחות/.../x.md -->` —
 *      these are source-path hints from the Fly.io import, not content.
 *   2. A leading `# title` header that duplicates the session heading /
 *      breadcrumb shown above the body by the page itself.
 *
 * Returns the cleaned markdown string, safe to hand to <GuideMarkdown>.
 */
export function cleanMarkdownBody(body: string | null | undefined): string {
  if (!body) return "";
  let s = body.replace(/\r\n/g, "\n");

  // Strip HTML comments (non-greedy, multi-line)
  s = s.replace(/<!--[\s\S]*?-->/g, "");

  // Trim leading blank space produced by the comment strip.
  s = s.replace(/^\s+/, "");

  // If the first non-blank line is an H1, drop it (the page already
  // shows the title in its own header). We only strip ONE such line.
  s = s.replace(/^#[ \t]+[^\n]*\n+/, "");

  return s.trim();
}

/**
 * Pull a short title and subtitle out of a markdown summary blob.
 *
 * Scans the raw body (BEFORE cleanMarkdownBody strips the H1) and
 * returns the first heading line as the title plus the first non-heading
 * sentence as the subtitle. Both are markdown-stripped to plain text.
 *
 * Used for compact session list rows that need a glanceable headline
 * without storing it as a separate column yet.
 */
export function extractTitleAndSubtitle(body: string | null | undefined): {
  title: string;
  subtitle: string;
} {
  if (!body) return { title: "", subtitle: "" };

  const lines = body
    .replace(/\r\n/g, "\n")
    .replace(/<!--[\s\S]*?-->/g, "")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s && !/^[-*_]{3,}$/.test(s));

  let title = "";
  let subtitle = "";

  for (const line of lines) {
    const headingMatch = line.match(/^#{1,6}[ \t]+(.+)$/);
    if (headingMatch) {
      const text = stripMd(headingMatch[1]);
      if (!title) {
        title = text;
        continue;
      }
      // ignore further headings — we want plain prose for subtitle
      continue;
    }
    if (!subtitle) {
      subtitle = stripMd(line);
      if (title) break;
    }
  }

  return { title: title.trim(), subtitle: subtitle.trim() };
}

function stripMd(s: string): string {
  let out = s
    .replace(/^[>\-*]\s+/g, "")
    .replace(/[*_`]+/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .trim();
  // If the line looks like a slug (hyphens connecting words, no spaces),
  // turn it back into prose. The session H1s in the import pipeline are
  // filename slugs like "פוסט-עוגה-בשכבות-אהבה-עצמית".
  if (out.includes("-") && !out.includes(" ")) {
    out = out.replace(/-/g, " ").trim();
  }
  return out;
}

/**
 * Extract two-letter initials from a Hebrew or English name.
 * "אור" → "א", "סיון ואורי" → "סו" (first letter of first two words),
 * "Smoke Lilach Client" → "SL".
 */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1);
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).trim();
}
